import { ButtonProps, Grid, IconButton, InputAdornment, MenuItem } from '@material-ui/core';
import { Pageview } from '@material-ui/icons';
import { FocusEvent, Fragment, default as React, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import { APIValidationError } from '../../../api/api';
import KeyboardDatePickerController from '../../../components/DatePicker/KeyboardDatePickerController';
import FormContainer from '../../../components/Forms/FormContainer';
import TextInput from '../../../components/Inputs/TextInput';
import TextInputController from '../../../components/Inputs/TextInputController/TextInputController';
import Loading from '../../../components/Loading/Loading';
import TransitionModal from '../../../components/Modal/TransitionModal';
import { RadioOptions } from '../../../components/RadioButtons/RadioButtonOptions';
import RadioButtons from '../../../components/RadioButtons/RadioButtons';
import SelectController from '../../../components/Select/SelectController';
import { DATE_FORMAT_DB } from '../../../constants/date';
import { permissionType } from '../../../constants/permissions_definitions';
import { GenericObj } from '../../../helpers/customHooks/useButtons';
import useValidatorAPI from '../../../helpers/customHooks/useValidatorAPI';
import { dateToDbDate, dbDateToDate } from '../../../helpers/dateHelper';
import { tFormKey, tKey, tProjectKey } from '../../../helpers/translate';
import {
  acceptInvoice,
  authorizeInvoiceAction,
  closeModal,
  confirmInvoice,
  downloadInvoiceContractAction,
  editInvoiceData,
  getInvoiceData,
  invoiceSetData,
  sendNewInvoice,
  toAccountingInvoiceAction,
} from '../../../redux/accounting/actions';
import { InvoiceData } from '../../../redux/accounting/definitions';
import {
  selectInvoice,
  selectInvoicesCalculatedTotal,
  selectInvoicesDetailBaseTotal,
  selectInvoicesDetailRetentionTotal,
  selectInvoicesDetailTotal,
  selectInvoicesRetentionTotal,
  selectLoading,
  selectValidationErrors,
} from '../../../redux/accounting/selectors';
import { selectIsNousCims } from '../../../redux/auth/selectors';
import { CodeNamePair } from '../../../redux/common/definitions';
import { TaxRetentionCodes } from '../../../redux/entity/definitions';
import { getAccountingMetadataAction } from '../../../redux/metadata/actions';
import {
  selectAccountingMetadataLoaded,
  selectPaymentControlDay,
  selectPaymentDay,
  selectPaymentTypeCodes,
} from '../../../redux/metadata/selectors';
import { selectUserCan } from '../../../redux/permissions/selectors';
import { AppState } from '../../../redux/root-reducer';
import StateCode from '../../../types/Invoices/StateCode';
import { InvoiceSchema } from '../../../validations/formSchema';
import styles from './invoiceForm.module.scss';
import InvoiceModificationModal from './InvoiceModificationModal';
import InvoiceTableDetails from './InvoiceTableDetails';
import RejectInvoiceModal from './RejectInvoiceModal/RejectInvoiceModal';
import UploadFileInvoice from './UploadFileInvoice';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import DetailFormMode from '../../../types/Invoices/DetailFormMode';
import { useButtonAction } from '../../../helpers/customHooks/useHookMethod';

interface InvoiceFormProps {
  invoice_id: number;
  loading: boolean;
  invoiceData: InvoiceData;
  validationErrors: APIValidationError | null;
  accountingMetadataLoaded: boolean;
  paymentTypeCodesMetadata: CodeNamePair[];
  paymentControlDay: number;
  paymentDay: number;
  hidden?: boolean;
  isNousCims: boolean;
  invoiceDetailBaseTotal: number;
  invoicesDetailRetentionTotal: number;
  invoicesDetailTotal: number;
  selectInvoicesRetentionTotal: number;
  selectInvoicesCalculatedTotal: number;
  error: string | null;
  modalView: boolean;
  setButtonProperty: (payload: GenericObj) => void;
  setData: (key: string, value: string | number) => void;
  editInvoiceData: (invoice: InvoiceData) => void;
  getAccountingMetadata: () => void;
  sendInvoice: (invoice: InvoiceData) => void;
  acceptInvoice: (invoice: InvoiceData) => void;
  confirmInvoice: (invoice: InvoiceData) => void;
  authorizeInvoice: (invoice: InvoiceData) => void;
  getInvoiceData: (id: number) => void;
  toAccountingInvoice: (invoice: InvoiceData) => void;
  userCan: (permission: permissionType) => boolean | undefined;
  closeModal: () => void;
  downloadInvoiceContract: (entity_id: number, contract_number: string) => void;
}

const modalObj = {
  view: false,
  title: '',
  children: <Fragment />,
};

const cleanButtons = {
  buttonOnSave: { hidden: true },
  buttonOnSend: { hidden: true },
  buttonOnAccept: { hidden: true },
  buttonOnReject: { hidden: true },
  buttonOnConfirm: { hidden: true },
  buttonToAccounting: { hidden: true },
  buttonOnTracking: { hidden: true },
  buttonAuthorize: { hidden: true },
};

const InvoiceForm: React.FC<InvoiceFormProps> = ({
  invoice_id,
  invoiceData,
  loading,
  validationErrors,
  accountingMetadataLoaded,
  paymentTypeCodesMetadata,
  hidden,
  isNousCims,
  invoiceDetailBaseTotal,
  invoicesDetailRetentionTotal,
  invoicesDetailTotal,
  selectInvoicesRetentionTotal,
  selectInvoicesCalculatedTotal,
  error,
  paymentControlDay,
  modalView,
  setData,
  setButtonProperty,
  editInvoiceData,
  getAccountingMetadata,
  sendInvoice,
  acceptInvoice,
  confirmInvoice,
  getInvoiceData,
  userCan,
  toAccountingInvoice,
  closeModal,
  authorizeInvoice,
  downloadInvoiceContract,
}) => {
  const {
    entity_id,
    invoice_number,
    reference_number,
    invoice_date,
    due_date,
    account_number,
    account_numbers,
    retention_code,
    retention_name,
    description,
    total_amount,
    supplier_code,
    entity_name,
    entity_contracts,
    contract_number,
    state_code,
    state_name,
    is_credit,
    payment_type_code,
    is_high_amount_invoice,
    file_id,
  } = invoiceData;

  const numberDay = new Date().getDate();

  const invoiceStateCode = new StateCode(state_code);

  const canCreate = userCan('allow_create_invoice');
  const canAccept = userCan('allow_accept_invoice') && invoiceStateCode.isPendiente() && isNousCims;
  const canConfirm = userCan('allow_confirm_invoice') && invoiceStateCode.isAceptada() && isNousCims;
  const canSendToAccounting = userCan('allow_invoice_to_accounting') && isNousCims;
  const canSendToSAGE = userCan('allow_send_invoice_pending_to_sage') && isNousCims;
  const canChangeInvoice = !!userCan('allowInvoiceChanges') && isNousCims;

  const detailFormMode = DetailFormMode.create(invoiceStateCode, canChangeInvoice);

  const canAuthorize = (): boolean => {
    if (userCan('allow_auth_high_amount_invoice')) return true;
    if (userCan('allow_auth_low_amount_invoice') && !is_high_amount_invoice) return true;
    return false;
  };

  const {
    errors,
    control,
    setError,
    watch,
    handleSubmit,
    clearError,
    triggerValidation,
    register,
    setValue,
    reset,
  } = useForm<InvoiceData>({
    mode: 'onSubmit',
    submitFocusError: true,
    validationSchema: InvoiceSchema,
    defaultValues: useMemo(() => {
      return invoiceData;
    }, [invoiceData]),
  });

  const [createdModal, setCreatedModal] = React.useState(modalObj);
  const [rejectModal, setRejectModal] = React.useState(false);
  const [readOnly, setReadOnly] = React.useState(true);

  const disableInvoice = !(invoiceStateCode.isEnCreacion() || invoiceStateCode.isPendiente());

  const radioOptions: RadioOptions[] = [
    { value: '0', label: tKey('Cargo') },
    { value: '1', label: tKey('Abono') },
  ];

  const onCloseModal = () => {
    clearError();
    closeModal();
  };

  const mergeInvoiceData = (data: InvoiceData): InvoiceData => {
    const {
      account_number,
      closing_date,
      contract_number,
      description,
      due_date,
      invoice_date,
      invoice_number,
      payment_type_code,
      reference_number,
      is_credit,
      total_amount,
      file_id,
    } = data;
    return {
      ...invoiceData,
      account_number,
      closing_date,
      contract_number,
      description,
      due_date,
      invoice_date,
      invoice_number,
      payment_type_code,
      reference_number,
      is_credit,
      total_amount,
      file_id,
    };
  };

  const handleSaveInvoice = (data: InvoiceData) => {
    clearError();
    editInvoiceData(mergeInvoiceData(data));
  };

  const handleSendInvoice = (data: InvoiceData) => {
    clearError();
    setButtonProperty({ ...cleanButtons });
    sendInvoice(mergeInvoiceData(data));
  };

  const handleAcceptInvoice = (data: InvoiceData) => {
    clearError();
    setButtonProperty({ ...cleanButtons });
    acceptInvoice(mergeInvoiceData(data));
  };

  const handleConfirmInvoice = (data: InvoiceData) => {
    clearError();
    setButtonProperty({ ...cleanButtons });
    confirmInvoice(mergeInvoiceData(data));
  };

  const handleToAccountingInvoice = (data: InvoiceData) => {
    clearError();
    setButtonProperty({ ...cleanButtons });
    toAccountingInvoice(mergeInvoiceData(data));
  };

  const handleAuthorizeInvoice = (data: InvoiceData) => {
    clearError();
    setButtonProperty({ ...cleanButtons });
    authorizeInvoice(mergeInvoiceData(data));
  };
  const buttonOnSave: ButtonProps = {
    hidden: !(invoiceStateCode.isEnCreacion() || (canAccept && invoiceStateCode.isPendiente())),
  };

  const buttonOnSend: ButtonProps = {
    hidden: !canCreate,
    disabled: false,
  };

  const buttonOnAccept: ButtonProps = {
    hidden: !canAccept,
    disabled: false,
  };

  const buttonOnConfirm: ButtonProps = {
    hidden: !canConfirm,
    disabled: false,
  };

  const buttonToAccounting: ButtonProps = {
    hidden: !(canSendToAccounting && invoiceStateCode.isConfirmada()),
    disabled: false,
  };

  const buttonAuthorize: ButtonProps = {
    hidden: !canAuthorize() && invoiceStateCode.isAContabilizar(),
    disabled: false,
  };

  const buttonOnReject: ButtonProps = {
    hidden: !(
      (canAccept || canConfirm || canSendToAccounting || canAuthorize() || canSendToSAGE) &&
      invoiceStateCode.canRejectInvoice()
    ),
    disabled: false,
    onClick: () => setRejectModal(true),
  };

  useButtonAction(buttonOnSave, handleSubmit(handleSaveInvoice));
  useButtonAction(buttonOnSend, handleSubmit(handleSendInvoice));
  useButtonAction(buttonOnAccept, handleSubmit(handleAcceptInvoice));
  useButtonAction(buttonOnConfirm, handleSubmit(handleConfirmInvoice));
  useButtonAction(buttonToAccounting, handleSubmit(handleToAccountingInvoice));
  useButtonAction(buttonAuthorize, handleSubmit(handleAuthorizeInvoice));

  const canModifyInvoice = detailFormMode.isInModifiableMode() && !invoiceData.closing_date;

  const buttonModify: ButtonProps = {
    hidden: !canModifyInvoice,
    onClick: () =>
      setCreatedModal({
        view: true,
        title: tKey('Modificar factura'),
        children: (
          <InvoiceModificationModal
            onSubmitOk={() => {
              setCreatedModal(modalObj);
            }}
            onCloseModal={() => {
              setCreatedModal(modalObj);
              onCloseModal();
            }}
          />
        ),
      }),
  };

  useEffect(() => {
    reset(invoiceData);
  }, [invoiceData, reset]);

  useEffect(() => {
    switch (invoiceData.state_code) {
      case StateCode.EN_CREACION:
        setReadOnly(!canCreate);
        setButtonProperty({
          buttonOnSave,
          buttonOnSend,
          buttonOnConfirm: { hidden: true },
          buttonOnAccept: { hidden: true },
          buttonOnReject: { hidden: true },
          buttonToAccounting: { hidden: true },
          buttonAuthorize: { hidden: true },
          buttonModify: { hidden: true },
        });

        break;
      case StateCode.PENDIENTE:
        setReadOnly(!canAccept);
        setButtonProperty({
          buttonOnSave,
          buttonOnReject,
          buttonModify,
          buttonOnAccept,
          buttonOnSend: { hidden: true },
          buttonOnConfirm: { hidden: true },
          buttonToAccounting: { hidden: true },
          buttonAuthorize: { hidden: true },
        });
        break;
      case StateCode.ACEPTADA:
        setReadOnly(true);
        if (!hidden) {
          setButtonProperty({
            buttonOnSave,
            buttonOnReject,
            buttonModify,
            buttonOnConfirm,
            buttonToAccounting: { hidden: true },
            buttonAuthorize: { hidden: true },
            buttonOnAccept: { hidden: true },
            buttonOnSend: { hidden: true },
          });
        }
        break;
      case StateCode.CONFIRMADA:
        setReadOnly(true);
        if (!hidden) {
          setButtonProperty({
            buttonOnSend: { hidden: true },
            buttonOnAccept: { hidden: true },
            buttonOnSave,
            buttonOnReject,
            buttonModify,
            buttonToAccounting,
            buttonOnConfirm: { hidden: true },
            buttonAuthorize: { hidden: true },
          });
        }
        break;
      case StateCode.A_CONTABILIZAR:
        setReadOnly(true);
        setButtonProperty({
          buttonOnSave: { hidden: true },
          buttonOnAccept: { hidden: true },
          buttonOnConfirm: { hidden: true },
          buttonToAccounting: { hidden: true },
          buttonAuthorize,
          buttonOnReject,
          buttonModify,
        });
        break;
      case StateCode.AUTORIZADA:
        setReadOnly(true);
        setButtonProperty({
          buttonAuthorize: { hidden: true },
          buttonOnSave: { hidden: true },
          buttonOnAccept: { hidden: true },
          buttonToAccounting: { hidden: true },
          buttonOnConfirm: { hidden: true },
          buttonOnReject,
          buttonModify,
        });
        break;
      case StateCode.PENDIENTE_ENVIO_SAGE:
      case StateCode.EN_PROCESO_DE_PAGO:
      case StateCode.PAGADA:
        setReadOnly(true);
        setButtonProperty({
          buttonOnSave: { hidden: true },
          buttonOnAccept: { hidden: true },
          buttonToAccounting: { hidden: true },
          buttonOnConfirm: { hidden: true },
          buttonAuthorize: { hidden: true },
          buttonOnReject: { hidden: true },
          buttonModify,
        });
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state_code, setReadOnly, validationErrors, error]);

  useValidatorAPI(validationErrors, setError);

  useEffect(() => {
    if (!accountingMetadataLoaded) {
      getAccountingMetadata();
    }
  }, [getAccountingMetadata, accountingMetadataLoaded]);

  useEffect(() => {
    if (invoice_id !== 0) {
      getInvoiceData(invoice_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice_id]);

  const [invoiceDetailsModal, setInvoiceDetailsModal] = React.useState({
    view: false,
    body: [''],
    children: <Fragment />,
    title: '',
  });

  useEffect(() => {
    if (!modalView) {
      setInvoiceDetailsModal(prevState => ({
        ...prevState,
        view: false,
      }));
    }
  }, [modalView]);

  if (loading) return <Loading big />;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSelectChange = (target: any) => {
    if (target.name) {
      setData(target.name, target.value);
    }
  };

  const handleSetDate = (name: string, date: MaterialUiPickersDate) => {
    if (date) {
      const formattedDate = date.format(DATE_FORMAT_DB);
      setData(name, formattedDate);
    }
  };

  const onBlurSetDate = (name: string, date?: string) => {
    if (date) {
      const formattedDate = dateToDbDate(date);
      setData(name, formattedDate);
    }
  };

  const renderPaymentTypeCode = (): JSX.Element => {
    if (isNousCims) {
      return (
        <SelectController
          control={control}
          errors={errors}
          size="20"
          schema={InvoiceSchema}
          name="payment_type_code"
          disabled={readOnly}
          ref={register}
          defaultValue={payment_type_code}
          label={tFormKey('Tipo de efecto')}
          onClick={e => handleSelectChange(e.target)}
        >
          {paymentTypeCodesMetadata.map(type => (
            <MenuItem key={type.code} value={type.code}>
              {type.name}
            </MenuItem>
          ))}
        </SelectController>
      );
    }
    return (
      <TextInputController
        type="hidden"
        control={control}
        key="payment_type_code"
        name="payment_type_code"
        errors={errors}
        schema={InvoiceSchema}
        defaultValue={payment_type_code}
        visible={false}
      />
    );
  };

  const downloadContract = () => {
    const contractNumber = contract_number || watch('contract_number');
    if (contractNumber) {
      downloadInvoiceContract(entity_id, contractNumber);
    }
  };

  const renderContractNumberElement = (): JSX.Element => {
    const label = tFormKey('Contrato');
    return readOnly ? (
      <TextInputController
        control={control}
        errors={errors}
        schema={InvoiceSchema}
        defaultValue={contract_number}
        name="contract_number"
        label={label}
        fullWidth
        disabled
      />
    ) : (
      <SelectController
        fullWidth
        control={control}
        errors={errors}
        schema={InvoiceSchema}
        ref={register}
        defaultValue={contract_number}
        name="contract_number"
        label={label}
        disabled={readOnly}
        onClick={e => handleSelectChange(e.target)}
      >
        {entity_contracts?.map((v, i) => (
          <MenuItem key={`${v}_${i}`} value={v}>
            {v}
          </MenuItem>
        ))}
      </SelectController>
    );
  };

  const renderContractNumber = (): JSX.Element => {
    return (
      <div className={styles.invoiceContractNumber}>
        {renderContractNumberElement()}
        <IconButton onClick={() => downloadContract()}>
          <Pageview className={styles.contractViewIcon} />
        </IconButton>
      </div>
    );
  };

  return (
    <div className={styles.invoiceFormContainer}>
      <FormContainer
        title={`${tKey('Datos Factura')}: ${invoice_number || tKey('Número pendiente')}`}
        subtitle={
          paymentControlDay < numberDay && !due_date
            ? tKey('Factura entrada fuera de plazo. El pago se efectuará en el mes siguiente.')
            : undefined
        }
      >
        <Grid container justify="flex-start">
          <Grid item>
            <TextInputController
              type="hidden"
              control={control}
              key="is_credit"
              name="is_credit"
              errors={errors}
              schema={InvoiceSchema}
              defaultValue={is_credit}
              visible={false}
            />
            <RadioButtons
              name="is_credit_rb"
              questionText=""
              disabled={readOnly}
              options={radioOptions}
              defaultValue={is_credit ? is_credit.toString() : '0'}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const isCredit = e.target.value === '1' ? 1 : 0;
                setData('is_credit', isCredit);
                setValue('is_credit', isCredit);
              }}
            />
          </Grid>
        </Grid>

        <TextInputController
          control={control}
          schema={InvoiceSchema}
          size="20"
          label={tFormKey('Número de factura')}
          name="reference_number"
          ref={register}
          defaultValue={reference_number}
          errors={errors}
          disabled={readOnly}
          onBlur={(e: FocusEvent<HTMLInputElement>) => {
            setData(e.target.name, e.target.value);
            triggerValidation('reference_number');
          }}
        />
        <KeyboardDatePickerController
          className={'inputs_size_20'}
          size="20"
          label={tFormKey('Fecha factura')}
          name="invoice_date"
          control={control}
          schema={InvoiceSchema}
          defaultValue={!!invoice_date ? dbDateToDate(invoice_date) : undefined}
          errors={errors}
          disabled={readOnly}
          onAccept={date => {
            handleSetDate('invoice_date', date);
          }}
          onBlur={e => {
            onBlurSetDate('invoice_date', e.target.value);
          }}
        />
        <KeyboardDatePickerController
          className={'inputs_size_20'}
          size="20"
          label={tFormKey('Fecha de vencimiento')}
          name="due_date"
          control={control}
          schema={InvoiceSchema}
          inputValue={!!due_date ? dbDateToDate(due_date) : undefined}
          errors={errors}
          disabled
        />
        <TextInput size="20" defaultValue={state_name} name="state_name" label={tFormKey('Estado')} disabled />
        <TextInput
          size="20"
          defaultValue={supplier_code}
          name="supplier_code"
          label={tFormKey('Código proveedor')}
          disabled
        />
        <TextInput size="50" defaultValue={entity_name} name="entity_name" label={tFormKey('Razón social')} disabled />
        {renderContractNumber()}
        <SelectController
          size={50}
          control={control}
          errors={errors}
          schema={InvoiceSchema}
          defaultValue={account_number}
          disabled={readOnly}
          name="account_number"
          label={tProjectKey('Número de cuenta')}
          onClick={e => handleSelectChange(e.target)}
        >
          {account_numbers?.map((iban: string, index: number) => (
            <MenuItem key={`${iban}_${index}`} value={iban}>
              {iban}
            </MenuItem>
          ))}
        </SelectController>
        {renderPaymentTypeCode()}
        <TextInputController
          control={control}
          schema={InvoiceSchema}
          label={tFormKey('Descripción')}
          name="description"
          ref={register}
          defaultValue={description}
          errors={errors}
          multiline
          rows="5"
          rowsMax="5"
          size="100"
          disabled={readOnly}
          onBlur={(e: FocusEvent<HTMLInputElement>) => {
            setData(e.target.name, e.target.value);
            triggerValidation('description');
          }}
        />
        <TextInputController
          size="20"
          type="number"
          control={control}
          schema={InvoiceSchema}
          label={tFormKey('Total a pagar')}
          name="total_amount"
          ref={register}
          defaultValue={total_amount}
          errors={errors}
          disabled={readOnly || disableInvoice}
          onBlur={(e: FocusEvent<HTMLInputElement>) => {
            setData(e.target.name, e.target.value);
            triggerValidation('total_amount');
          }}
        />
        <TextInput
          type="number"
          size="20"
          value={invoiceDetailBaseTotal}
          name="invoiceDetailBaseTotal"
          label={tFormKey('Total base')}
          InputProps={{
            endAdornment: <InputAdornment position="end">€</InputAdornment>,
          }}
          disabled
        />
        <TextInput
          type="number"
          size="20"
          value={invoicesDetailRetentionTotal}
          name="invoicesDetailRetentionTotal"
          label={tFormKey('Total impuestos')}
          InputProps={{
            endAdornment: <InputAdornment position="end">€</InputAdornment>,
          }}
          disabled
        />
        <TextInput
          type="number"
          size="20"
          value={invoicesDetailTotal}
          name="invoicesDetailTotal"
          label={tFormKey('Total detalle')}
          InputProps={{
            endAdornment: <InputAdornment position="end">€</InputAdornment>,
          }}
          disabled
        />
        <Grid container justify="flex-end" spacing={3}>
          {retention_code && retention_code !== TaxRetentionCodes.SIN_RETENCION ? (
            <Fragment>
              <Grid item xs={3}>
                <TextInput
                  size="100"
                  defaultValue={retention_name}
                  name="retention_name"
                  label={tFormKey('Tipo de retención')}
                  hidden={!retention_code}
                  disabled
                />
              </Grid>
              <Grid item xs={3}>
                <TextInput
                  type="number"
                  size="100"
                  value={selectInvoicesRetentionTotal}
                  name="selectInvoicesRetentionTotal"
                  label={tFormKey('Total Retención')}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">€</InputAdornment>,
                  }}
                  disabled
                />
              </Grid>
            </Fragment>
          ) : null}
          <Grid item xs={3}>
            <TextInput
              type="number"
              size="100"
              value={selectInvoicesCalculatedTotal}
              name="selectInvoicesRetentionTotal"
              label={tFormKey('Total Factura')}
              InputProps={{
                endAdornment: <InputAdornment position="end">€</InputAdornment>,
              }}
              disabled
            />
          </Grid>
        </Grid>
        <div className={styles.invoiceFormContainer}>
          <UploadFileInvoice invoice_id={invoiceData.id} readOnly={readOnly} errors={errors} />
          <TextInputController
            type="hidden"
            control={control}
            key="file_id"
            name="file_id"
            errors={errors}
            schema={InvoiceSchema}
            value={file_id}
            visible={false}
          />
        </div>
        <div className={styles.invoiceDetailsContainer}>
          <InvoiceTableDetails detailFormMode={detailFormMode} readOnly={readOnly} />
        </div>
      </FormContainer>
      <TransitionModal
        view={invoiceDetailsModal.view}
        handleClose={onCloseModal}
        bodyModal=" "
        title={tKey('Nueva línea')}
      >
        {invoiceDetailsModal.children}
      </TransitionModal>
      <TransitionModal
        view={createdModal.view}
        handleClose={() => setCreatedModal(modalObj)}
        title={createdModal.title}
      >
        {createdModal.children}
      </TransitionModal>
      <RejectInvoiceModal
        show={rejectModal}
        handleClose={() => setRejectModal(false)}
        invoice_id={invoiceData.id}
        current_state={invoiceData.state_code}
      />
    </div>
  );
};

const mapStateToProps = (state: AppState) => ({
  loading: selectLoading(state),
  invoiceData: selectInvoice(state),
  validationErrors: selectValidationErrors(state),
  error: state.accountingReducer.error,
  accountingMetadataLoaded: selectAccountingMetadataLoaded(state),
  paymentTypeCodesMetadata: selectPaymentTypeCodes(state),
  paymentControlDay: selectPaymentControlDay(state),
  paymentDay: selectPaymentDay(state),
  userCan: selectUserCan(state),
  isNousCims: selectIsNousCims(state),
  invoiceDetailBaseTotal: selectInvoicesDetailBaseTotal(state),
  invoicesDetailRetentionTotal: selectInvoicesDetailRetentionTotal(state),
  invoicesDetailTotal: selectInvoicesDetailTotal(state),
  selectInvoicesRetentionTotal: selectInvoicesRetentionTotal(state),
  selectInvoicesCalculatedTotal: selectInvoicesCalculatedTotal(state),
  modalView: state.accountingReducer.modalView,
});
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapDispatchToProps = (dispatch: any) => ({
  setData: (key: string, value: string | number) => dispatch(invoiceSetData(key, value)),
  editInvoiceData: (invoice: InvoiceData) => dispatch(editInvoiceData(invoice)),
  getAccountingMetadata: () => dispatch(getAccountingMetadataAction()),
  sendInvoice: (invoice: InvoiceData) => dispatch(sendNewInvoice(invoice)),
  acceptInvoice: (invoice: InvoiceData) => dispatch(acceptInvoice(invoice)),
  authorizeInvoice: (invoice: InvoiceData) => dispatch(authorizeInvoiceAction(invoice)),
  confirmInvoice: (invoice: InvoiceData) => dispatch(confirmInvoice(invoice)),
  toAccountingInvoice: (invoice: InvoiceData) => dispatch(toAccountingInvoiceAction(invoice)),
  getInvoiceData: (id: number) => dispatch(getInvoiceData(id)),
  closeModal: () => dispatch(closeModal()),
  downloadInvoiceContract: (entity_id: number, contract_number: string) =>
    dispatch(downloadInvoiceContractAction(entity_id, contract_number)),
});

export default connect(mapStateToProps, mapDispatchToProps)(InvoiceForm);
